import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Banner() {
  const {
    t
  } = useLanguage();
  const photos = [{
    src: 'https://cdn.magicpatterns.com/uploads/rgw9wRXHbHCC4m3Zu1jJwd/IMG_8608.jpg',
    alt: 'Curly top fade'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/3TqLVZmkAeDyknyTFDddwG/IMG_8601.jpg',
    alt: 'Sharp line fade'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/b3kYiNcCS7S2xRY49q78au/IMG_8603.jpg',
    alt: 'Kids cut with design'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/1gDaTypJ35iSV3gZWeGqzP/IMG_8589.jpg',
    alt: 'Buzz cut with beard lineup'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/ah1rVReSH6pwJnpCMxx1oE/IMG_8592.jpg',
    alt: 'Curly top with beard'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/uXpsXWVPYKPPbZyYMGTYb3/IMG_8587.jpg',
    alt: 'Mid fade with curls'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/bQb2pVb4VBGJwoAXiVsHAP/IMG_8593.jpg',
    alt: 'Kids fade with design line'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/mrtugHDxiB1gcn1U4iJ7UW/IMG_8594.jpg',
    alt: 'Curly top with V design'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/deRRqzSRuEJbWRvRJ1CaoB/IMG_8599.jpg',
    alt: 'Clean skin fade'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/g7gKR8XSZ9Ny79VqC7EGe3/IMG_8595.jpg',
    alt: 'Skin fade with beard shape'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/vmaCvepFkFdnru2h6Y3J42/IMG_8588.jpg',
    alt: 'Kids classic cut'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/gA972TqYssSWcvyZzrNkTt/IMG_8609.jpg',
    alt: 'Kids fade with hard part'
  }];
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  function next() {
    setDirection(1);
    setCurrent(prev => (prev + 1) % photos.length);
  }
  function prev() {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + photos.length) % photos.length);
  }
  // Auto-advance every 3.5 seconds
  useEffect(() => {
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, []);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.95
    })
  };
  // Show 3 thumbnails: prev, current, next
  const prevIndex = (current - 1 + photos.length) % photos.length;
  const nextIndex = (current + 1) % photos.length;
  return <section className="py-16 md:py-24 px-4 md:px-8 bg-barber-dark border-y border-barber-lightGray" data-id="element-39">
      <div className="max-w-4xl mx-auto" data-id="element-40">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-41">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-barber-text uppercase mb-4" data-id="element-42">
            {t('banner.heading1')}{' '}
            <span className="text-barber-gold" data-id="element-43">{t('banner.heading2')}</span>
          </h2>
          <p className="text-barber-muted text-lg md:text-xl" data-id="element-44">
            {t('banner.subtitle')}
          </p>
          <div className="w-16 h-1 bg-barber-gold mx-auto mt-4" data-id="element-45"></div>
        </motion.div>

        {/* Main Slider */}
        <div className="relative" data-id="element-46">
          {/* Main Image */}
          <div className="relative w-full max-w-lg mx-auto aspect-square bg-black rounded-sm overflow-hidden border border-barber-lightGray" data-id="element-47">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-48">
              <motion.img key={current} src={photos[current].src} alt={photos[current].alt} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-49" />
            </AnimatePresence>

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-barber-black/40 via-transparent to-transparent pointer-events-none" data-id="element-50"></div>

            {/* Counter badge */}
            <div className="absolute top-3 right-3 bg-barber-black/70 text-barber-gold text-xs font-heading font-bold px-3 py-1 rounded-full border border-barber-gold/30" data-id="element-51">
              {current + 1} / {photos.length}
            </div>
          </div>

          {/* Arrow Buttons */}
          <button onClick={prev} className="absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 bg-barber-black/80 hover:bg-barber-gold/20 border border-barber-lightGray hover:border-barber-gold text-barber-gold p-2 rounded-full transition-all z-10" aria-label="Previous" data-id="element-52">
            <ChevronLeftIcon size={24} data-id="element-53" />
          </button>
          <button onClick={next} className="absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 bg-barber-black/80 hover:bg-barber-gold/20 border border-barber-lightGray hover:border-barber-gold text-barber-gold p-2 rounded-full transition-all z-10" aria-label="Next" data-id="element-54">
            <ChevronRightIcon size={24} data-id="element-55" />
          </button>
        </div>

        {/* Thumbnail Grid — All photos visible */}
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mt-6" data-id="element-56">
          {photos.map((photo, index) => <button key={index} onClick={() => {
          setDirection(index > current ? 1 : -1);
          setCurrent(index);
        }} className={`aspect-square rounded-sm overflow-hidden border-2 transition-all bg-black ${index === current ? 'border-barber-gold shadow-[0_0_12px_rgba(212,168,83,0.4)] scale-105' : 'border-barber-lightGray opacity-50 hover:opacity-80'}`} data-id="element-57">
              <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" data-id="element-58" />
            </button>)}
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-1.5 mt-4" data-id="element-59">
          {photos.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > current ? 1 : -1);
          setCurrent(index);
        }} className={`w-2 h-2 rounded-full transition-all ${index === current ? 'bg-barber-gold w-6' : 'bg-barber-lightGray hover:bg-barber-muted'}`} aria-label={`Go to photo ${index + 1}`} data-id="element-60" />)}
        </div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="font-heading text-lg md:text-2xl text-barber-gold uppercase tracking-widest text-center mt-8" data-id="element-61">
          {t('banner.overlay')}
        </motion.p>
      </div>
    </section>;
}